<template>
  <v-app :class="[browserhackClass]">
    <v-overlay
      :value="overlay"
      style="z-index: 1000000"
    >
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      top
      @click.native="snackbar = false"
    >
      <div class="d-flex flex-row">
        <v-icon
          :color="snackbarTextColor"
          class="pr-3 mb-1"
        >
          {{ snackbarIcon }}
        </v-icon>
        <span :class="`${snackbarTextColor}--text text-body-1 font-weight-medium text-center`">{{ snackbarText }}</span>
      </div>
    </v-snackbar>

    <router-view v-if="$route.meta?.public === true || $route.meta?.single === true" />
    <main-view v-else />

    <v-snackbar
      v-if="updateAvailable"
      :value="updateAvailable"
      bottom
      style="z-index: 100001"
      dark
      :timeout="-1"
    >
      {{ $t('label.app_update_available') }}
      <template #action>
        <v-btn
          color="blue"
          elevation="0"
          small
          @click="refreshApp"
        >
          {{ $t('button.update_now') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Dynamically Load Styles for hacked CSS scrollbars on windows chrome -->
    <browserhack-scrollbar v-if="applyBrowserhackScrollbar" />
  </v-app>
</template>

<script setup lang="ts">
import {
  computed, defineAsyncComponent, getCurrentInstance, onMounted, ref, watch,
} from 'vue';
import usePwaInstall from '@/composables/pwa-install';
import createApps from './util/create-apps';
import setLanguage from './plugins/i18n-locale';
import TimeZone from './model/TimeZone';
import { useVuetify } from './components/helpers/vue2-helpers';
import i18n from './plugins/i18n';
import store from './store/store';
import useActivityIndicator from './composables/activity-indicator';
import useBrowserHacks from './components/browserhacks/browserhacks';
import useToastInit from './composables/toast-init';
import usePwaUpdate from './composables/pwa-update';
import appVersion from '@/app-version';

usePwaInstall();

const BrowserhackScrollbar = defineAsyncComponent(
  () => import('@/components/browserhacks/BrowserhackScrollbar.vue'),
) as any;
const { applyBrowserhackScrollbar, browserhackClass } = useBrowserHacks();
const vuetify = useVuetify();
const { overlay } = useActivityIndicator();

const {
  snackbar, snackbarText, snackbarIcon, snackbarColor, snackbarTextColor, snackbarTimeout,
} = useToastInit();

const { updateAvailable, refreshApp } = usePwaUpdate();

// Handle Locale Change
const timeZoneCountry = computed(() => {
  const tzid = store.getters.timeZoneId;
  console.log(`[LOCALE] tzid: ${tzid}`);
  if (tzid === undefined) { return 'en-US'; }
  const cc = store.getters.sortedTimezones.find((tz: TimeZone) => tz.id === tzid)?.countryCode || 'US';
  console.log(`[LOCALE] ${cc}`);
  return cc;
});

watch(timeZoneCountry, () => {
  console.log(`[LOCALE] changed country: ${timeZoneCountry.value}`);
  setLanguage(i18n.locale.split('-')[0], timeZoneCountry.value ?? '');
});

// Add lang tag to document when locale changes
const locale = computed(() => i18n.locale);

const updateDocumentLanguage = () => { document.documentElement.lang = locale.value; };

watch(locale, () => {
  setLanguage(i18n.locale.split('-')[0], timeZoneCountry.value ?? '');
  updateDocumentLanguage();
});

//

// Add Root theme--dark class when theme is switched
const dark = computed(() => vuetify.theme.dark);

const updateDocumentTheme = () => {
  if (dark.value) {
    document.documentElement.classList.add('theme--dark');
  } else {
    document.documentElement.classList.remove('theme--dark');
  }
};

watch(dark, () => updateDocumentTheme());
//

// Update title of page with account name
const accountName = computed(() => store.state.settings.account.accountName);

const updatePageTitle = () => {
  const appTitle = import.meta.env.VITE_TITLE || '';
  document.title = accountName.value ? `${appTitle} - ${accountName.value}` : appTitle;
};

watch(accountName, () => updatePageTitle());

// Init language from default
setLanguage(i18n.locale.split('-')[0], timeZoneCountry.value ?? '');

onMounted(() => {
  // Insert App Version into Document Code as Comment
  const version = `${import.meta.env.VITE_TITLE} (${appVersion})`;
  document.documentElement.appendChild(document.createComment(version));

  updateDocumentTheme();
  updateDocumentLanguage();
  updatePageTitle();
  createApps(getCurrentInstance()!.proxy);
});

</script>

<style lang="scss">
  @import './components/browserhacks/browserhacks.scss';
</style>
